import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import PhoneFrame from '../PhoneFrame';

/**
 * The ecosystem hero: one claim, then the whole architecture in a single
 * composition — the "Continue as User / Coach" door front and center,
 * the coach dashboard fanned left, the member dashboard fanned right,
 * all standing on one shared ground glow. Two audiences, one door.
 */
const useClusterWidth = () => {
  const get = () =>
    typeof window !== 'undefined'
      ? Math.min(860, window.innerWidth - 48)
      : 860;
  const [w, setW] = useState(get);
  useEffect(() => {
    const onResize = () => setW(get());
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return w;
};

const Hero = () => {
  const reduce = useReducedMotion();
  const stageRef = useRef(null);
  const clusterW = useClusterWidth();

  const centerW = Math.round(Math.min(300, Math.max(168, clusterW * 0.35)));
  const sideW = Math.round(centerW * 0.77);

  // Full animations everywhere for now; set this to `reduce || clusterW < 720`
  // to make the hero paint statically on phones again.
  const staticMode = reduce;

  // Gentle whole-cluster parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 16 });
  const sy = useSpring(my, { stiffness: 50, damping: 16 });
  const rotateY = useTransform(sx, [-0.5, 0.5], [-4, 4]);
  const rotateX = useTransform(sy, [-0.5, 0.5], [3, -3]);

  const handleMove = (e) => {
    if (staticMode) return;
    const r = stageRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };

  const rise = (delay = 0) =>
    staticMode
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
        };

  const float = (dur, dist, delay = 0) =>
    staticMode
      ? {}
      : {
          animate: { y: [0, -dist, 0] },
          transition: { duration: dur, repeat: Infinity, ease: 'easeInOut', delay },
        };

  return (
    <section
      id="hero"
      className="relative overflow-hidden bg-ston-bg pt-28 md:pt-32 pb-16 md:pb-24"
      data-testid="hero-section"
    >
      {/* Ambience */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        {/* Ambient glow as a pure gradient — a filtered blur here stalls WebKit for seconds */}
        <div
          className="absolute -top-48 left-1/2 -translate-x-1/2 w-[1100px] h-[720px] rounded-full"
          style={{
            background:
              'radial-gradient(closest-side, rgba(147,51,234,0.13), rgba(147,51,234,0.06) 55%, transparent 78%)',
          }}
        />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ston-purple/25 to-transparent" />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 text-center">
        {/* ---------- Copy ---------- */}
        <motion.span
          className="inline-flex items-center gap-2.5 section-label"
          {...rise(0)}
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-ston-purple-light opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-ston-purple-light" />
          </span>
          PERFORMANCE OS
        </motion.span>

        <motion.h1
          className="mt-6 font-outfit font-extrabold italic uppercase leading-[0.96]"
          style={{ fontSize: 'clamp(34px, 5.4vw, 78px)', letterSpacing: '-0.03em' }}
          {...rise(0.08)}
        >
          <span className="block text-ston-text">Everyone tracks.</span>
          <span
            className="block whitespace-nowrap"
            style={{
              // capped so it never wraps on narrow screens; desktop uses the h1 size
              fontSize: 'min(1em, 7.2vw)',
              background:
                'linear-gradient(100deg, #c4b5fd 0%, #a855f7 45%, #818cf8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Nobody understands.
          </span>
        </motion.h1>

        <motion.p
          className="mt-6 mx-auto max-w-[62ch] text-[16.5px] md:text-[18.5px] leading-relaxed text-ston-text-secondary"
          {...rise(0.18)}
        >
          One platform — for people who want to{' '}
          <span className="text-ston-text font-medium">understand their health</span>,
          and for the{' '}
          <span className="text-ston-text font-medium">professionals who guide them</span>.
        </motion.p>

        {/* ---------- CTAs ---------- */}
        <motion.div
          className="mt-9 flex flex-wrap items-center justify-center gap-4"
          {...rise(0.28)}
        >
          <Link
            to="/product"
            className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-ston-purple to-ston-purple-light px-8 py-4 text-[15px] font-semibold text-white transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_36px_rgba(147,51,234,0.5)]"
            data-testid="hero-user-cta"
          >
            <span>I want to be healthy</span>
            <span
              aria-hidden="true"
              className="transition-transform duration-300 group-hover:translate-x-0.5"
            >
              →
            </span>
          </Link>
          <Link
            to="/product?view=coach"
            className="group inline-flex items-center gap-2.5 rounded-full border border-white/[0.14] bg-white/[0.04] px-7 py-4 text-[15px] font-semibold text-ston-text backdrop-blur-sm transition-all duration-300 hover:border-ston-purple/50 hover:bg-ston-purple/10"
            data-testid="hero-professional-cta"
          >
            <span>I'm a health professional</span>
            <span
              aria-hidden="true"
              className="transition-transform duration-300 group-hover:translate-x-0.5"
            >
              →
            </span>
          </Link>
        </motion.div>

        {/* ---------- The ecosystem: MacBook + iPhone, front and center ---------- */}
        <motion.div
          ref={stageRef}
          className="relative mt-16 md:mt-20 mx-auto"
          style={{ width: clusterW }}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          {...(staticMode
            ? {}
            : {
                initial: { opacity: 0, y: 40 },
                animate: { opacity: 1, y: 0 },
                transition: { duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] },
              })}
        >
          {/* Shared ground glow */}
          <div
            aria-hidden="true"
            className="absolute left-1/2 -translate-x-1/2 bottom-[-12%] pointer-events-none"
            style={{
              width: '112%',
              height: '48%',
              background:
                'radial-gradient(ellipse 50% 50% at 50% 50%, rgba(147,51,234,0.2), transparent 70%)',
              }}
          />

          {/* Reflective floor — desktop only; the reflection doubles raster cost on phones */}
          <div
            style={
              clusterW >= 700
                ? {
                    WebkitBoxReflect:
                      'below 8px linear-gradient(transparent 70%, rgba(255,255,255,0.06))',
                  }
                : undefined
            }
          >
            <motion.div
              style={staticMode ? {} : { rotateY, rotateX, transformStyle: 'preserve-3d' }}
              className="relative"
            >
              {/* The trio: coach dashboard · the door · member dashboard */}
              <div className="flex items-end justify-center" style={{ perspective: 1600 }}>

                {/* Coach dashboard — left wing */}
                <motion.div
                  className="relative opacity-90"
                  {...(staticMode
                    ? {}
                    : {
                        initial: { opacity: 0, x: centerW * 0.5, scale: 0.92 },
                        animate: { opacity: 0.9, x: 0, scale: 1 },
                        transition: { duration: 0.9, delay: 0.95, ease: [0.16, 1, 0.3, 1] },
                      })}
                >
                  <motion.div
                    {...float(8, 4, 0.9)}
                    style={{ transform: 'rotateY(16deg) translateY(24px)' }}
                  >
                    <PhoneFrame
                      src="/screens/coach-home.webp"
                      alt="Coach dashboard — four clients waiting, action queue, pending check-ins"
                      width={sideW}
                      glowColor="rgba(74,222,128,0.18)"
                    />
                  </motion.div>
                </motion.div>

                {/* The door — Continue as User or Coach */}
                <motion.div
                  className="relative z-10 -mx-6 md:-mx-8"
                  {...(staticMode
                    ? {}
                    : {
                        initial: { opacity: 0, y: 44 },
                        animate: { opacity: 1, y: 0 },
                        transition: { duration: 0.9, delay: 0.55, ease: [0.16, 1, 0.3, 1] },
                      })}
                >
                  <motion.div {...float(7, 5, 0.4)}>
                    <PhoneFrame
                      src="/screens/splash-continue.webp"
                      alt="STON.FIT — continue as User to track training, nutrition and recovery, or as Coach to manage clients, plans and progress"
                      width={centerW}
                      glowColor="rgba(147,51,234,0.32)"
                      priority
                    />
                  </motion.div>
                </motion.div>

                {/* Member dashboard — right wing */}
                <motion.div
                  className="relative opacity-90"
                  {...(staticMode
                    ? {}
                    : {
                        initial: { opacity: 0, x: -centerW * 0.5, scale: 0.92 },
                        animate: { opacity: 0.9, x: 0, scale: 1 },
                        transition: { duration: 0.9, delay: 0.95, ease: [0.16, 1, 0.3, 1] },
                      })}
                >
                  <motion.div
                    {...float(8, 4, 0.5)}
                    style={{ transform: 'rotateY(-16deg) translateY(24px)' }}
                  >
                    <PhoneFrame
                      src="/screens/home.webp"
                      alt="Member dashboard — daily momentum 41 percent, today's signals"
                      width={sideW}
                      glowColor="rgba(147,51,234,0.22)"
                    />
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* The one-system caption — two controlled lines on phones, one on desktop */}
          <motion.p
            className="relative z-30 mt-10 md:mt-12 font-mono text-[10px] md:text-[12px] uppercase tracking-[1.5px] md:tracking-[3px] text-ston-text-muted leading-relaxed"
            {...rise(1.7)}
          >
            <span className="block md:inline whitespace-nowrap text-ston-text-secondary">
              The app for health professionals
            </span>
            <span className="hidden md:inline mx-3 text-ston-purple-light">·</span>
            <span className="block md:inline mt-1 md:mt-0 whitespace-nowrap text-ston-text-secondary">
              and people who want to be healthy
            </span>
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
